import { type ApolloError, useQuery } from '@apollo/client';
import { Order_By } from '@openx/types/gqlTypes';
import { useCallback, useMemo } from 'react';
import { GetSegmentsDocument } from '../../../../../../graphql/query/audiences/GetSegments.generated';
import { DemographicSegments, type FormSegment } from '../../../../../../modules/audiences/AudienceForm/types';
import { getDemographicSegments } from '../utils/getDemographicSegments';

const MIN_AGE = 19;

export const ANY_AGE_VALUE = 0;
export const ANY_GENDER_VALUE = 'Any';

export type AgeType = {
  min: number;
  max: number;
};

export type UseDemographicsOptionsResponse = {
  loading: boolean;
  error?: ApolloError;
  options: {
    ageOptions: FormSegment[];
    genderOptions: FormSegment[];
  };
  rangeToSegments: (ageRange: AgeType) => FormSegment[];
  ageList: number[];
};

export const useDemographicsOptions = (): UseDemographicsOptionsResponse => {
  const {
    loading,
    error,
    data: { segment = [] } = {},
  } = useQuery(GetSegmentsDocument, {
    variables: {
      order_by: [{ full_name: Order_By.Asc }],
      where: {
        category: { _in: [DemographicSegments.AGE, DemographicSegments.GENDER] },
        is_archived: { _eq: false },
        _or: [
          {
            status: { _eq: 'ready' },
          },
          { is_nav_only: { _eq: true } },
        ],
      },
    },
  });

  const demographicSegments = useMemo(() => getDemographicSegments(segment), [segment]);

  const rangeToSegments = useCallback(
    ({ min, max }: AgeType): FormSegment[] => demographicSegments.ageOptions.slice(min - MIN_AGE, max - MIN_AGE + 1),
    [demographicSegments]
  );

  const ageList = useMemo(() => {
    const sortedAges = demographicSegments.ageOptions.map(({ name }) => +name).sort((a, b) => a - b);

    return [ANY_AGE_VALUE, ...sortedAges];
  }, [demographicSegments.ageOptions]);

  return {
    rangeToSegments,
    loading,
    error,
    options: demographicSegments,
    ageList,
  };
};
